import React, { useState, useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import "./LandingPage.css";

const LandingPage = () => {
  const history = useHistory();
  const location = useLocation();
  const [activeScenario, setActiveScenario] = useState(0);
  const heroRef = useRef(null);
  
  const appDownloadUrl = `${window.location.origin}/app-download`;
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(appDownloadUrl)}`;
  
  
  const scenarios = [
    {
      number: "01",
      title: "A Homemaker looking to hire.",
      description: "A mother wants to make her daughter's birthday special. She needs a photographer who can capture memories beautifully - quickly and affordably. With GigFWS, she finds the perfect.",
      image: "/assets/images/Sceanrio-one-Image.svg",
    },
    {
      number: "02",
      title: "A Startup needs a Freelancer.",
      description: "With tight budget and timelines, A bootstrapped startup looking to hire a freelance developer to complete a project. With GigFWS, not only they post a job with less than Rs. 100 but they got a reliable one from many job quotes that they have received.",
      image: "/assets/images/Sceanrio-two-image.svg",
    },
    {
      number: "03",
      title: "Talented Person with Portfolio Career.",
      description: "A developer by education, A photographer by passion and A skilled gardener by family owned business, he is looking to build his portfolio career and increase is income streams and better utilize his free times. With GigFWS, he finds the best independent marketplace and get jobs with zero commission to pay.",
      image: "/assets/images/Sceanario-three-image.svg",
    },
  ];

  const scenarioSectionRef = useRef(null);
  const scrollDeltaAccumulator = useRef(0);
  const isTransitioningRef = useRef(false);
  const transitionTimeoutRef = useRef(null);
  const handlerRef = useRef(null);
  const currentScenarioRef = useRef(0);
  const lastTransitionTimeRef = useRef(0);
  const hasEnteredSectionRef = useRef(false);
  
  const touchStartYRef = useRef(0);
  const touchAccumulatorRef = useRef(0);
  const isInScenarioSectionRef = useRef(false);
  const touchHandlerRef = useRef(null);

  useEffect(() => {
    currentScenarioRef.current = activeScenario;
  }, [activeScenario]);

  // Handles clicking on a scenario item to switch to that scenario
  const handleScenarioClick = (index) => {
    if (index !== activeScenario) {
      setActiveScenario(index);
      lastTransitionTimeRef.current = Date.now();
    }
  };

  useEffect(() => {
    if (location.pathname !== '/') {
      return;
    }

    const scenarioObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const wasInSection = isInScenarioSectionRef.current;
          isInScenarioSectionRef.current = entry.intersectionRatio >= 0.3;
          
          // When entering the section, reset to first scenario
          if (!wasInSection && isInScenarioSectionRef.current) {
            hasEnteredSectionRef.current = true;
            setActiveScenario(0);
            currentScenarioRef.current = 0;
            scrollDeltaAccumulator.current = 0;
            touchAccumulatorRef.current = 0;
          }
          
          // When leaving the section, reset flag
          if (wasInSection && !isInScenarioSectionRef.current) {
            hasEnteredSectionRef.current = false;
          }
        });
      },
      {
        threshold: [0.3, 0.5, 0.7, 1.0],
        rootMargin: '0px'
      }
    );

    // Handles mouse wheel scroll events to navigate between scenarios
    const handleWheel = (e) => {
      if (!scenarioSectionRef.current || !isInScenarioSectionRef.current) {
        return;
      }
      
      const now = Date.now();
      const delta = e.deltaY;
      
      // Check if we're at boundaries and should allow main scroll
      const isAtTop = currentScenarioRef.current === 0;
      const isAtBottom = currentScenarioRef.current === 2;
      const isScrollingUp = delta < 0;
      const isScrollingDown = delta > 0;
      
      // Allow main scroll only at boundaries
      if ((isAtTop && isScrollingUp) || (isAtBottom && isScrollingDown)) {
        return; // Let the main scroll happen
      }
      
      // Prevent main scroll for all other cases
      e.preventDefault();
      e.stopPropagation();
      
      // Reduced cooldown for smoother continuous scrolling
      if (now - lastTransitionTimeRef.current < 400) {
        return;
      }
      
      if ((scrollDeltaAccumulator.current > 0 && delta < 0) || (scrollDeltaAccumulator.current < 0 && delta > 0)) {
        scrollDeltaAccumulator.current = 0;
      }
      
      scrollDeltaAccumulator.current += delta;

      // Lower threshold for more responsive continuous scrolling
      if (Math.abs(scrollDeltaAccumulator.current) >= 25) {
        const accDelta = scrollDeltaAccumulator.current;
        scrollDeltaAccumulator.current = 0;
        
        let shouldTransition = false;
        let nextScenario = currentScenarioRef.current;
        
        if (accDelta > 0) {
          if (currentScenarioRef.current < 2) {
            nextScenario = currentScenarioRef.current + 1;
            shouldTransition = true;
          }
        } else {
          if (currentScenarioRef.current > 0) {
            nextScenario = currentScenarioRef.current - 1;
            shouldTransition = true;
          }
        }
        
        if (shouldTransition) {
          lastTransitionTimeRef.current = now;
          
          setActiveScenario(nextScenario);
          
          if (transitionTimeoutRef.current) {
            clearTimeout(transitionTimeoutRef.current);
          }
        }
      }
    };

    // Handles touch start event to initialize touch position for scenario navigation
    const handleTouchStart = (e) => {
      if (!scenarioSectionRef.current || !isInScenarioSectionRef.current) {
        return;
      }
      touchStartYRef.current = e.touches[0].clientY;
      touchAccumulatorRef.current = 0;
    };

    // Handles touch move events to navigate between scenarios on mobile devices
    const handleTouchMove = (e) => {
      if (!scenarioSectionRef.current || !isInScenarioSectionRef.current) {
        return;
      }

      const touchY = e.touches[0].clientY;
      const touchDelta = touchStartYRef.current - touchY;
      
      const now = Date.now();
      
      // Check if we're at boundaries and should allow main scroll
      const isAtTop = currentScenarioRef.current === 0;
      const isAtBottom = currentScenarioRef.current === 2;
      const isScrollingUp = touchDelta < 0;
      const isScrollingDown = touchDelta > 0;
      
      // Allow main scroll only at boundaries
      if ((isAtTop && isScrollingUp) || (isAtBottom && isScrollingDown)) {
        return; // Let the main scroll happen
      }
      
      // Prevent main scroll for all other cases
      e.preventDefault();
      
      // Reduced cooldown for smoother continuous scrolling
      if (now - lastTransitionTimeRef.current < 400) {
        return;
      }

      if ((touchAccumulatorRef.current > 0 && touchDelta < 0) || 
          (touchAccumulatorRef.current < 0 && touchDelta > 0)) {
        touchAccumulatorRef.current = 0;
      }
      
      touchAccumulatorRef.current += touchDelta;
      touchStartYRef.current = touchY;

      // Lower threshold for more responsive touch scrolling
      const scrollThreshold = 35;
      
      if (Math.abs(touchAccumulatorRef.current) >= scrollThreshold) {
        const accDelta = touchAccumulatorRef.current;
        touchAccumulatorRef.current = 0;
        
        let shouldTransition = false;
        let nextScenario = currentScenarioRef.current;
        
        if (accDelta > 0) {
          if (currentScenarioRef.current < 2) {
            nextScenario = currentScenarioRef.current + 1;
            shouldTransition = true;
          }
        } else {
          if (currentScenarioRef.current > 0) {
            nextScenario = currentScenarioRef.current - 1;
            shouldTransition = true;
          }
        }
        
        if (shouldTransition) {
          lastTransitionTimeRef.current = now;
          setActiveScenario(nextScenario);
          
          if (transitionTimeoutRef.current) {
            clearTimeout(transitionTimeoutRef.current);
          }
        }
      }
    };

    // Resets touch accumulator when touch interaction ends
    const handleTouchEnd = () => {
      touchAccumulatorRef.current = 0;
    };

    // Global wheel handler to prevent body scroll when in scenario section
    const handleGlobalWheel = (e) => {
      if (!scenarioSectionRef.current || !isInScenarioSectionRef.current) {
        return;
      }
      
      // Check if the event target is within the scenario section
      if (scenarioSectionRef.current.contains(e.target)) {
        return; // Let the section's own handler deal with it
      }
      
      const delta = e.deltaY;
      const isAtTop = currentScenarioRef.current === 0;
      const isAtBottom = currentScenarioRef.current === 2;
      const isScrollingUp = delta < 0;
      const isScrollingDown = delta > 0;
      
      // Allow main scroll only at boundaries
      if ((isAtTop && isScrollingUp) || (isAtBottom && isScrollingDown)) {
        return; // Let the main scroll happen
      }
      
      // Prevent main scroll for all other cases
      e.preventDefault();
      e.stopPropagation();
    };

    handlerRef.current = handleWheel;
    touchHandlerRef.current = { start: handleTouchStart, move: handleTouchMove, end: handleTouchEnd };

    // Add document-level listener to catch all scroll events
    document.addEventListener('wheel', handleGlobalWheel, { passive: false });

    if (scenarioSectionRef.current) {
      scenarioSectionRef.current.addEventListener('wheel', handleWheel, { passive: false });
      scenarioSectionRef.current.addEventListener('touchstart', handleTouchStart, { passive: true });
      scenarioSectionRef.current.addEventListener('touchmove', handleTouchMove, { passive: false });
      scenarioSectionRef.current.addEventListener('touchend', handleTouchEnd, { passive: true });
      scenarioObserver.observe(scenarioSectionRef.current);
    }

    return () => {
      document.removeEventListener('wheel', handleGlobalWheel);
      if (scenarioSectionRef.current) {
        if (handlerRef.current) {
          scenarioSectionRef.current.removeEventListener('wheel', handlerRef.current);
        }
        if (touchHandlerRef.current) {
          scenarioSectionRef.current.removeEventListener('touchstart', touchHandlerRef.current.start);
          scenarioSectionRef.current.removeEventListener('touchmove', touchHandlerRef.current.move);
          scenarioSectionRef.current.removeEventListener('touchend', touchHandlerRef.current.end);
        }
        scenarioObserver.unobserve(scenarioSectionRef.current);
      }
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }
    };
  }, [location.pathname]);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.5, 
      rootMargin: "0px 0px 150px 0px", 
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            entry.target.classList.add("visible");
          }, 100);
        }
      });
    }, observerOptions);

    const elements = document.querySelectorAll("[data-animate]");
    elements.forEach((el) => observer.observe(el));

    return () => {
      elements.forEach((el) => observer.unobserve(el));
    };
  }, []);



  return (
    <div className="landing-page">
      <section className="hero-section" ref={heroRef} data-animate id="hero">
        <div className="landing-page-container">
          <div className="hero-content">
            <div className="hero-left">
              <h1 className="hero-title fade-in-up">GigFWS</h1>
              <p className="hero-subtitle fade-in-up delay-1">
                India's Most Independent Gig B2B & B2C Platform
              </p>
              <div className="hero-buttons fade-in-up delay-2">
                <button 
                  className="btn-post-gig"
                  onClick={() => {
                    if (window.openRegisterModal) {
                      window.openRegisterModal('landing_page');
                    } else {
                      history.push("/register");
                    }
                  }}
                >
                  Register
                </button>
                <button 
                  className="btn-find-gig"
                  onClick={() => {
                    if (window.openLoginModal) {
                      window.openLoginModal('landing_page');
                    } else {
                      history.push("/login");
                    }
                  }}
                >
                  Login
                </button>
              </div>
              <div className="qr-section fade-in-up delay-3">
                <div className="qr-card">
                  <div className="qr-card-left">
                    <div className="qr-code-wrapper">
                      <div className="qr-code">
                        <div className="qr-placeholder">
                        <img 
                          src={qrCodeUrl} 
                          alt="QR Code for App Download" 
                          className="qr-code-image"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="qr-card-right">
                    <div className="qr-text-container">
                      <p className="qr-text-line1">Scan QR to</p>
                      <p className="qr-text-line2">Download the app</p>
                    </div>
                    <div className="app-store-buttons">
                      <a 
                        href={appDownloadUrl}
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="app-store-btn google-play-btn"
                      >
                        <img src="/imageslan/playstore.png" alt="Google Play" className="store-button-icon play-store-btn-icon" />
                      </a>
                      <a 
                        href="https://apps.apple.com/in/app/gigfws-post-apply-gig-jobs/id6752988240" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="app-store-btn app-store-btn-link"
                      >
                        <img src="/imageslan/Group 3107@2x.png" alt="App Store" className="store-button-icon app-store-btn-icon" />
                      </a> 
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="hero-right fade-in delay-4">
              <div className="phone-graphic">
                <div className="phone-mockup-wrapper">
                  <img 
                    src="/assets/images/Free_Realistic_iPhone_16_Pro_in_Hand_Mockup 3.svg" 
                    alt="iPhone Mockup" 
                    className="phone-mockup-image"
                  />
                </div>
                <div className="gradient-overlay">
                  <div className="overlay-badge">
                    <img 
                      src="/assets/images/PloygonBadge.svg" 
                      alt="Badge" 
                      className="overlay-badge-bg"
                    />
                    <div className="overlay-badge-content">
                      <span className="overlay-text-large">0%</span>
                      <div className="overlay-text-small">
                        <span>Commission</span>
                        <span>for Gigs</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="workforce-section" data-animate id="workforce">
        <div className="landing-page-container">
          <div className="workforce-content">
            <div className="workforce-image-wrapper fade-in-left">
              <img src="/assets/images/WorkForceImage.svg" alt="Workforce" className="workforce-image" style={{borderRadius: '18px'}} />
            </div>
            <div className="workforce-text fade-in-right">
              <h2 className="workforce-title" style={{ lineHeight: '1.2' }}>
                Join us in shaping the revolution of the future workforce.
              </h2>
              <p className="workforce-description" style={{ lineHeight: '1.6' }}>
                We are redefining traditional education-based jobs by creating a hybrid of skills, experience, and education-driven opportunities.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="slogan-section" data-animate id="slogan">
        <div className="landing-page-container">
          <div className="slogan-content">
            <h2 className="slogan-main fade-in-up">Work & Hire Made Simple</h2>
            <div className="slogan-highlight fade-in-up delay-1">
              <span className="gigfws-badge">GigFWS</span>
              <span className="bridges-text">Bridges</span>
            </div>
            <p className="slogan-end fade-in-up delay-2">Every Dream With An Opportunity.</p>
          </div>
        </div>
      </section>

      <section className="scenario-section" data-animate id="scenarios" ref={scenarioSectionRef}>
        <div className="landing-page-container">
          <div className="scenario-content">
            {scenarios.map((scenario, index) => {
              const isActive = activeScenario === index;
              return (
              <div
                key={index}
                className={`scenario-item ${isActive ? "active" : ""}`}
                data-scenario-index={index}
                data-active={isActive}
                onClick={() => handleScenarioClick(index)}
                style={{ cursor: 'pointer' }}
              >
                <div className="scenario-indicator">
                  <div className={`scenario-number-circle ${activeScenario === index ? "active" : ""}`}>
                    {activeScenario === index && <span className="scenario-number-text">{scenario.number}</span>}
                  </div>
                  {!isActive && (
                    <div className="scenario-label">
                      SCENARIO {index === 0 ? "ONE" : index === 1 ? "TWO" : "THREE"}
                    </div>
                  )}
                </div>
                <div className={`scenario-content-wrapper ${activeScenario === index ? "active" : ""}`}>
                  <div className="scenario-text">
                    {isActive && (
                      <>
                        <div className="scenario-label scenario-label-active">
                          SCENARIO {index === 0 ? "ONE" : index === 1 ? "TWO" : "THREE"}
                        </div>
                        <h3 className="scenario-title">{scenario.title}</h3>
                        <p className="scenario-description" style={{ fontSize: 'calc(100% + 2px)', marginTop: '0.5em' }}>{scenario.description}</p>
                      </>
                    )}
                    {!isActive && (
                      <>
                        <h3 className="scenario-title">{scenario.title}</h3>
                        <p className="scenario-description" style={{ fontSize: 'calc(100% + 2px)', marginTop: '0.5em' }}>{scenario.description}</p>
                      </>
                    )}
                  </div>
                  <div className="scenario-image">
                    <img src={scenario.image} alt={scenario.title} className="scenario-image-img" />
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="platform-slogan-section" data-animate id="platform-slogan">
        <div className="landing-page-container">
          <div className="platform-content">
            <h2 className="platform-main fade-in-up">Different People, Different Needs.</h2>
            <p className="platform-sub fade-in-up delay-1">
              One Platform. Where Talent Meets Opportunity.
            </p>
          </div>
        </div>
      </section>

      <section className="cta-banner" data-animate id="cta">
        <div className="landing-page-container">
          <div className="cta-content fade-in-up">
            <p className="cta-top-text" style={{ lineHeight: '1.6' }}>
              Whether you are looking to build your Portfolio Career or to choose the right one from the crowd for your job,
            </p>
            <h2 className="cta-main-text">
              GigFWS can support you with verified users and secured payments
            </h2>
            <button 
              className="btn-know-more"
              onClick={(e) => {
                e.preventDefault();
                if (window.openRegisterModal) {
                  window.openRegisterModal('landing_page_cta_banner');
                } else {
                  history.push("/register");
                }
              }}
            >
              Join us now
            </button>
          </div>
        </div>
      </section>

    </div>
  );
};

export default LandingPage;
